import React, { useMemo, useState } from 'react';
import { PlusIcon, TagIcon, Trash2Icon } from 'lucide-react';
import { Product } from '../../types/pro';
import { krPrecise } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProProductsProps {
  products: Product[];
  onAdd: (product: Omit<Product, 'id'>) => void;
  onUpdate: (id: string, changes: Partial<Product>) => void;
  onRemove: (id: string) => void;
}
const emptyDraft = {
  productNo: '',
  name: '',
  description: '',
  unit: 'timer',
  price: '',
  vatRate: '0.25'
};

/**
 * The price list the invoice editor searches. Kept apart from materials:
 * materials have a cost price and a margin, products are simply what a line
 * on an invoice costs.
 */
export function ProProducts({
  products,
  onAdd,
  onUpdate,
  onRemove
}: ProProductsProps) {
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return products.filter(product => term ? [product.name, product.productNo, product.description].join(' ').toLowerCase().includes(term) : true).sort((a, b) => a.productNo.localeCompare(b.productNo, 'da'));
  }, [products, query]);
  const nextProductNo = () => {
    const numbers = products.map(product => parseInt(product.productNo.replace(/\D/g, ''), 10)).filter(value => !Number.isNaN(value));
    const next = numbers.length === 0 ? 1 : Math.max(...numbers) + 1;
    return String(next).padStart(3, '0');
  };
  const submit = () => {
    if (!draft.name.trim()) return;
    onAdd({
      productNo: draft.productNo.trim() || nextProductNo(),
      name: draft.name.trim(),
      description: draft.description.trim(),
      unit: draft.unit.trim() || 'stk',
      price: parseFloat(draft.price) || 0,
      vatRate: parseFloat(draft.vatRate)
    });
    setDraft(emptyDraft);
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const cell = 'w-full rounded border border-transparent bg-transparent px-2 py-1.5 text-sm outline-none hover:border-stone-200 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500';
  return <div data-id="element-1853" data-mp="3kglz9tb75eno">
      <PageHeader title="Produkter og ydelser" subtitle={`${products.length} linjer — foreslås automatisk når du skriver på en faktura`} data-id="element-1854" data-mp="3kgc7q1ai6zfv" />

      <SearchField value={query} onChange={setQuery} placeholder="Søg produkt eller varenummer…" className="mb-4 max-w-md" data-id="element-1855" data-mp="35l5yl5g42kib" />

      {rows.length === 0 ? <EmptyState icon={TagIcon} title="Ingen produkter matcher" data-id="element-1856" data-mp="1u5zjk7yvto59" /> : <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-1857" data-mp="1z412mswb5ngq">
          <table className="w-full min-w-[760px] text-sm" data-id="element-1858" data-mp="h3jjag03akgj">
            <thead data-id="element-1859" data-mp="c6jd8538phpf">
              <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-1860" data-mp="c4ykml0hk8u7">
                {['Nr.', 'Navn', 'Beskrivelse', 'Enhed', 'Pris', 'Moms'].map(heading => <th key={heading} scope="col" className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1861" data-mp="m58xu5mitg9d">
                      {heading}
                    </th>)}
                <th className="w-10" data-id="element-1862" data-mp="77q7wzy3wx0c" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100" data-id="element-1863" data-mp="2fyg6m37sl8m">
              {rows.map(product => <tr key={product.id} data-id="element-1864" data-mp="3av8f5lbt5ac6">
                  <td className="w-16 px-3 py-1.5 font-mono text-xs text-stone-500" data-id="element-1865" data-mp="3au76fni9kzxm">
                    {product.productNo}
                  </td>
                  <td className="px-2 py-1.5" data-id="element-1866" data-mp="3jk5y2g1vlzzl">
                    <input value={product.name} onChange={e => product.id && onUpdate(product.id, {
                name: e.target.value
              })} aria-label="Produktnavn" className={`${cell} font-medium text-stone-900`} data-id="element-1867" data-mp="3jlh12dnhz3pn" />
                  </td>
                  <td className="px-2 py-1.5" data-id="element-1868" data-mp="71z9v798mgj4">
                    <input value={product.description} onChange={e => product.id && onUpdate(product.id, {
                description: e.target.value
              })} placeholder="Valgfri" aria-label="Beskrivelse" className={`${cell} text-stone-600`} data-id="element-1869" data-mp="b3b3dnv8flwl" />
                  </td>
                  <td className="w-24 px-2 py-1.5" data-id="element-1870" data-mp="2mtn610gfg50v">
                    <input value={product.unit} onChange={e => product.id && onUpdate(product.id, {
                unit: e.target.value
              })} aria-label="Enhed" className={cell} data-id="element-1871" data-mp="2fa471kqsg16n" />
                  </td>
                  <td className="w-32 px-2 py-1.5" data-id="element-1872" data-mp="2y8q9b2y1jyc5">
                    <input type="number" step="0.01" value={product.price} onChange={e => product.id && onUpdate(product.id, {
                price: parseFloat(e.target.value) || 0
              })} aria-label="Pris" className={`${cell} text-right font-medium tabular-nums`} data-id="element-1873" data-mp="2y7f7h8o5p0no" />
                  </td>
                  <td className="w-24 px-2 py-1.5" data-id="element-1874" data-mp="3w29frto4uiml">
                    <select value={product.vatRate} onChange={e => product.id && onUpdate(product.id, {
                vatRate: parseFloat(e.target.value)
              })} aria-label="Momssats" className={cell} data-id="element-1875" data-mp="3w3kgtlq71ca0">
                      <option value={0.25} data-id="element-1876" data-mp="3ccz1mk41c5ql">25 %</option>
                      <option value={0} data-id="element-1877" data-mp="3h58m3kmdsdlh">0 %</option>
                    </select>
                  </td>
                  <td className="px-1" data-id="element-1878" data-mp="33edai0khzie7">
                    <button onClick={() => product.id && onRemove(product.id)} aria-label={`Slet ${product.name}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1879" data-mp="33fewh031xpap">
                      <Trash2Icon className="h-4 w-4" data-id="element-1880" data-mp="2ydpmzovh9joq" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      <section className="mt-5 rounded-xl border border-stone-200 bg-white p-5" data-id="element-1881" data-mp="2asx3iv5w2hsn">
        <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-1882" data-mp="2osg7rcvkx8zo">
          Tilføj produkt eller ydelse
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6" data-id="element-1883" data-mp="2jtv5z90oih94">
          <input value={draft.productNo} onChange={e => setDraft({
          ...draft,
          productNo: e.target.value
        })} placeholder={nextProductNo()} aria-label="Varenummer" className={field} data-id="element-1884" data-mp="2xqi6tmvr5rkb" />
          <input value={draft.name} onChange={e => setDraft({
          ...draft,
          name: e.target.value
        })} placeholder="Navn" aria-label="Navn" className={`${field} col-span-2`} data-id="element-1885" data-mp="32i9vtsz9fp8c" />
          <input value={draft.unit} onChange={e => setDraft({
          ...draft,
          unit: e.target.value
        })} placeholder="Enhed" aria-label="Enhed" className={field} data-id="element-1886" data-mp="3chilwar08jpf" />
          <input type="number" value={draft.price} onChange={e => setDraft({
          ...draft,
          price: e.target.value
        })} placeholder="Pris" aria-label="Pris" className={field} data-id="element-1887" data-mp="221dge2x0qi8c" />
          <select value={draft.vatRate} onChange={e => setDraft({
          ...draft,
          vatRate: e.target.value
        })} aria-label="Momssats" className={field} data-id="element-1888" data-mp="1ee5jo9dnohhx">
            <option value="0.25" data-id="element-1889" data-mp="1objnn9ie7dtc">Moms 25 %</option>
            <option value="0" data-id="element-1890" data-mp="1o9qla6etbfr5">Moms 0 %</option>
          </select>
          <input value={draft.description} onChange={e => setDraft({
          ...draft,
          description: e.target.value
        })} placeholder="Beskrivelse (valgfri)" aria-label="Beskrivelse" className={`${field} col-span-2 lg:col-span-6`} data-id="element-1891" data-mp="1t3bi3v4o63or" />
        </div>
        <button onClick={submit} disabled={!draft.name.trim()} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-1892" data-mp="3du613nrr9mk4">
          <PlusIcon className="h-4 w-4" data-id="element-1893" data-mp="2zz3ko6pa8et2" />
          Tilføj til prislisten
        </button>
        <p className="mt-2 text-xs text-stone-400" data-id="element-1894" data-mp="2uzr1px1j7rvr">
          Priser angives altid ekskl. moms. Momssatsen følger med på
          fakturalinjen. Sidste pris: {krPrecise(parseFloat(draft.price) || 0)}
        </p>
      </section>
    </div>;
}